package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import dev.lingvo.shared.DecideProposalsRequest
import dev.lingvo.shared.ProposalDto
import dev.lingvo.shared.WorkspaceDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectAuthority
import dev.lingvo.site.state.ProjectAuthoritySubject
import dev.lingvo.site.state.VerifiedProjectRoute
import dev.lingvo.site.state.actionKey
import dev.lingvo.site.state.liveProjectRoute
import dev.lingvo.site.state.runProjectAction
import dev.lingvo.site.api.Session
import kotlinx.coroutines.launch
import dev.lingvo.site.i18n.t

/** The header and per-card controls share one admission, transport and retained receipt. */
internal class ProposalDecisions(
    val state: ProposalsDecisionState,
    val decide: (ids: List<String>, runId: String?, accept: Boolean) -> Unit,
) {
    val busy: Boolean get() = state.busy
    val blocked: Boolean get() = state.readRequired
    val message: String? get() = state.failure
}

@Composable
internal fun ProposalDecisionSurface(
    proposals: List<ProposalDto>,
    authority: ProjectAuthority,
    total: Int = proposals.size,
    actionStateKey: ProjectAuthoritySubject,
    pageClaim: ProjectActionClaim?,
    slug: String,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    /** Existing injected Unit seam stays compatible; it can never supply identified results. */
    decideProposals: (suspend (String, DecideProposalsRequest) -> Unit)? = null,
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    decisions: ProposalDecisions? = null,
    projectOverviewHref: String? = null,
    showRunActions: Boolean = true,
    showFeedback: Boolean = true,
    onChanged: () -> Unit,
) {
    val own = if (decisions == null) proposalDecisions(
        actionStateKey = actionStateKey,
        pageClaim = pageClaim,
        slug = slug,
        knownProposals = proposals,
        currentRoute = currentRoute,
        currentMemberships = currentMemberships,
        decideProposals = decideProposals,
        launchAction = launchAction,
        onChanged = onChanged,
    ) else null
    val active = decisions ?: own ?: return
    if (showFeedback) ProposalsAcceptedDrafts(slug, active.state, onChanged)
    // Confirmed drafts already provide the next step. An empty pending list must not replace
    // that review handoff with another invitation to connect the agent that just supplied them.
    if (proposals.isEmpty() && active.state.accepted.isNotEmpty()) return
    ProposalList(
        proposals = proposals,
        canDecide = authority.canDecideProposals,
        busy = active.busy || active.blocked,
        total = total,
        showRunActions = showRunActions,
        acknowledged = active.state.acknowledged,
        projectOverviewHref = projectOverviewHref,
        onDecide = active.decide,
    )
}

@Composable
internal fun proposalDecisions(
    actionStateKey: ProjectAuthoritySubject,
    pageClaim: ProjectActionClaim?,
    slug: String,
    knownProposals: List<ProposalDto> = emptyList(),
    state: ProposalsDecisionState? = null,
    currentRoute: () -> VerifiedProjectRoute? = { liveProjectRoute(slug) },
    currentMemberships: () -> List<WorkspaceDto> = { Session.organizations },
    decideProposals: (suspend (String, DecideProposalsRequest) -> Unit)? = null,
    launchAction: ((suspend () -> Unit) -> Unit)? = null,
    onChanged: () -> Unit,
): ProposalDecisions {
    val scope = rememberCoroutineScope()
    val activeState = state ?: remember(actionStateKey) { ProposalsDecisionState() }
    val launch: (suspend () -> Unit) -> Unit = launchAction ?: { action -> scope.launch { action() } }
    return ProposalDecisions(activeState) { ids, runId, accept ->
        val claim = pageClaim?.takeIf { it.subject == actionStateKey }
            ?.takeIf { it.remainsAuthorized(currentRoute(), currentMemberships()) }
        if (claim != null && !activeState.busy && !activeState.readRequired && ids.none { it in activeState.acknowledged }) {
            val request = DecideProposalsRequest(ids = ids, runId = runId, accept = accept)
            val capturedProposals = knownProposals.toList()
            activeState.busy = true
            launch {
                runProjectAction(
                    claim = claim,
                    currentRoute = { currentRoute()?.takeIf { it.actionKey() == actionStateKey.action } },
                    currentMemberships = currentMemberships,
                    setBusy = { activeState.busy = it },
                    transport = {
                        if (decideProposals != null) {
                            decideProposals(slug, request)
                            null
                        } else ApiClient.decideProposalsWithReceipt(slug, request)
                    },
                    onSuccess = { response ->
                        val receipt = response?.let { validatedProposalsReceipt(it, slug, request, capturedProposals) }
                        if (receipt == null) activeState.unconfirmed()
                        else {
                            activeState.confirmed(receipt)
                            onChanged()
                        }
                    },
                    onFailure = { error ->
                        activeState.unconfirmed(error.message?.takeIf(String::isNotBlank) ?: tr(
                            "app.projects.slug.proposals_decisions.couldnt_confirm_this_decision_reload_the",
                            "Couldn't confirm this decision. Reload the proposals before making another decision.",
                        ))
                    },
                )
            }
        }
    }
}
